import parseISO from 'date-fns/parseISO';
import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import {graphql, PageProps} from 'gatsby';
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews';

import A11y from '@src/components/A11y';
import Hero from '@src/components/Hero';
import PageTheme, {pageThemes} from '@src/components/PageTheme';
import PageHero from '@src/components/Hero/PageHero';
import PageSection from '@src/components/PageSection';
import GridOneCol from '@src/components/Grid/GridOneCol';
import GridTwoCol from '@src/components/Grid/GridTwoCol';
import BreadCrumbs from '@src/components/BreadCrumbs';
import PageLayout from '@src/components/PageLayout';
import BasicCardCta from './BasicCardCta';
import BasicChecklist from './BasicChecklist';
import IFrameEmbed from './IFrameEmbed';
import BasicDefinitionlist from './BasicDefinitionlist';
import TeamList from './TeamList';
import ArticleCards from './ArticleCards';
import BackgroundImageHero from './BackgroundImageHero';

import type {
  ArticleCardFragment,
  BasicCardCtaFragment,
  BasicChecklistFragment,
  BasicDefinitionlistFragment,
  BasicOneColFragment,
  BasicTeamListFragment,
  IFrameEmbedFragment,
  BackgroundImageHeroFragment,
  PageQueryQuery,
  IconListFragment,
  PageFindAPlanFragment,
  VideoCarouselFragment,
  LeadGenFormSliceFragment,
  ContentTilesFragment,
} from '@graphql-types';

import * as styles from './basicPage.module.scss';

import type {Translations} from '@src/i18n';
import IconList from './IconList';
import FindAPlan from './FindAPlan';
import VideoCarousel from './VideoCarousel';
import LeadGenFormSlice from './LeadGenForm';
import ContentTiles from './ContentTiles';

const getPageTheme = (prismicPage: PageQueryQuery['prismicPage']) => {
  const theme = prismicPage.data.theme
    ? prismicPage.data.theme.toLowerCase()
    : 'white';
  // "as const" on array forces this double cast so it can be treated as a string[] again...
  // ...which then must be cast back to the expected union type
  return (pageThemes as unknown as string[]).includes(theme)
    ? (theme as (typeof pageThemes)[number])
    : 'white';
};

const isEmptyContent = (content?: string) => {
  if (!content) {
    return true;
  }

  if (content === '<p></p>') {
    return true;
  }

  return false;
};

const BasicPage = ({
  data: {
    allLanguages: {nodes: languages},
    allPbps: {nodes: planBenefitPackages},
    allPrismicMainNavigation: navigation,
    allPrismicNotifications: notifications,
    prismicPage,
    site: {
      siteMetadata: {
        lastBuildTime,
        leadGenEndpoint,
        siteName,
        siteUrl,
        ssaCountiesForPbps: ssaCounties,
        supportedCounties,
        webhookAuth,
      },
    },
  },
  location,
  pageContext: {translations},
}: PageProps<
  PageQueryQuery,
  {
    translations: Translations[];
  }
>) => {
  const pageHeading = prismicPage.data.page_heading.text || null;
  const pageTitle = prismicPage.data.seo_title.text || pageHeading;
  const metaDescription = prismicPage.data.seo_description.text;
  const pageHero = prismicPage.data.page_hero[0];
  const pageTheme = getPageTheme(prismicPage);
  const useHeroFadeTheme = pageTheme === 'white';
  const headerStyle =
    prismicPage.data.header_style === 'Larger orange' ? 'bigOrangeHeaders' : '';
  const sliceContent = prismicPage.data.body;
  let sliceHTML: React.ReactNode[] = [];
  const showBreadcrumbs = prismicPage.data.show_breadcrumbs;

  if (sliceContent) {
    sliceHTML = sliceContent.map((slice, sliceIndex) => {
      switch (slice.slice_type) {
        case 'one_column':
          return (
            <PageSection key={slice.id}>
              <GridOneCol
                gridType="small"
                columnContent={slice as BasicOneColFragment}
              />
            </PageSection>
          );

        case 'two_column':
          return (
            <PageSection key={slice.id}>
              <GridTwoCol columnContent={slice.primary} />
            </PageSection>
          );

        case 'checklist':
          return (
            <BasicChecklist
              basicChecklistContent={slice as BasicChecklistFragment}
              key={slice.id}
            />
          );

        case 'definition_list':
          return (
            <BasicDefinitionlist
              basicDefinitionlistContent={slice as BasicDefinitionlistFragment}
              key={slice.id}
            />
          );

        case 'card_cta':
          const noBottomPadding = sliceIndex === sliceContent.length - 1;
          return (
            <BasicCardCta
              basicCardCtaContent={slice as BasicCardCtaFragment}
              noBottomPadding={noBottomPadding}
              key={slice.id}
            />
          );

        case 'iframe_embed':
          return (
            <IFrameEmbed
              frameEmbedContent={slice as IFrameEmbedFragment}
              key={slice.id}
            />
          );

        case 'team_list':
          return (
            <TeamList slice={slice as BasicTeamListFragment} key={slice.id} />
          );

        case 'article_cards':
          return (
            <ArticleCards slice={slice as ArticleCardFragment} key={slice.id} />
          );

        case 'background_image_hero':
          return (
            <BackgroundImageHero
              {...(slice as BackgroundImageHeroFragment)}
              key={slice.id}
            />
          );

        case 'icon_list':
          return <IconList {...(slice as IconListFragment)} key={slice.id} />;

        case 'find_a_plan_form':
          return (
            <FindAPlan
              data={slice as PageFindAPlanFragment}
              key={slice.id}
              planBenefitPackages={planBenefitPackages}
              ssaCounties={ssaCounties}
              supportedCounties={supportedCounties}
            />
          );

        case 'video_carousel':
          return (
            <VideoCarousel
              {...(slice as VideoCarouselFragment)}
              key={slice.id}
            />
          );

        case 'lead_gen_form':
          return (
            <LeadGenFormSlice
              key={slice.id}
              languages={languages}
              leadGenEndpoint={leadGenEndpoint}
              webhookAuth={webhookAuth}
              slice={slice as LeadGenFormSliceFragment}
            />
          );

        case 'content_tiles':
          return (
            <ContentTiles {...(slice as ContentTilesFragment)} key={slice.id} />
          );

        default:
          console.warn('No component matching slice type: ', slice.slice_type);
          return null;
      }
    });
  }

  return (
    <PageLayout
      className={styles.wrapper}
      languages={languages}
      lastBuildTime={parseISO(lastBuildTime)}
      lastUpdatedDate={prismicPage.last_publication_date}
      location={location}
      navigation={navigation}
      notifications={notifications}
      siteName={siteName}
      siteUrl={siteUrl}
      translations={translations}>
      <Helmet>
        <title>{pageTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
      </Helmet>

      <A11y title={pageTitle} />

      <PageTheme theme={pageTheme}>
        {showBreadcrumbs === 'Yes' && (
          <BreadCrumbs
            slug={location && location.pathname}
            title={pageHeading}
          />
        )}

        {pageHeading && <h1>{pageHeading}</h1>}

        {pageHero && !isEmptyContent(pageHero.hero_text.html) && (
          <Hero theme={useHeroFadeTheme ? 'fade' : undefined}>
            <PageHero heroContent={pageHero} />
          </Hero>
        )}

        <div className={`${styles.contentWrapper} ${styles[headerStyle]}`}>
          {sliceHTML}
        </div>
      </PageTheme>
    </PageLayout>
  );
};

export default withPrismicPreview(BasicPage);

export const query = graphql`
  query PageQuery($uid: String!) {
    ...BasicPageSlices
    ...PageLayout

    prismicPage(uid: {eq: $uid}) {
      _previewable
      uid
      lang
      data {
        seo_title {
          text
        }
        seo_description {
          text
        }
        page_name
        show_breadcrumbs
        page_heading {
          text
        }
        page_hero {
          hero_text {
            html
            text
          }
          hero_quote {
            text
          }
          hero_image {
            url
          }
          cta_text
          cta_link {
            url
          }
        }
        body {
          ...basicOneCol
          ...basicTwoCol
          ...basicChecklist
          ...basicCardCta
          ...basicDefinitionlist
          ...basicTeamList
          ...iFrameEmbed
          ...articleCard
          ...backgroundImageHero
          ...iconList
          ...pageFindAPlan
          ...videoCarousel
          ...leadGenFormSlice
          ...contentTiles
        }
        show_newsletter_signup_cta
        theme
        header_style
      }
      last_publication_date(formatString: "MM.DD.YYYY")
    }
  }

  fragment BasicPageSlices on Query {
    allPbps {
      nodes {
        csnp_conditions {
          type
        }
        name
        plan {
          ma_variant
          plan_type
        }
        record_locator
        service_areas {
          county_code
        }
        year
      }
    }
    site {
      siteMetadata {
        leadGenEndpoint
        ssaCountiesForPbps {
          code
          display_name
          state_abbreviation
          zip_codes {
            zip_code
          }
        }
        supportedCounties {
          county_code
          pages {
            lang
            path
          }
        }
        webhookAuth
      }
    }
  }
`;

export const basicOneColFragment = graphql`
  fragment basicOneCol on PrismicPageDataBodyOneColumn {
    id
    slice_type
    primary {
      border_color
      text_alignment
      rich_text_content {
        html
        text
      }
      full_width_image {
        url
      }
    }
  }
`;

export const basicTwoColFragment = graphql`
  fragment basicTwoCol on PrismicPageDataBodyTwoColumn {
    id
    slice_type
    primary {
      border_color
      grid_type
      left_column {
        html
        text
      }
      right_column {
        html
        text
      }
    }
  }
`;
